import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { SharedStorageService } from 'common/util/module'
import { ArchiveEndpointRequest } from 'elements-sdk'
import { IdentityService } from 'main/services/identity.service'
import { TapeJobsService } from './tapeJobs.service'
import { NotifyService } from 'common/util/services/notify.service'

export interface TapeArchiveQueueItem {
  path: string
  include: 'all'|'not-archived'|'archived-once'|'not-in-group'
  options?: {
    group_name?: string
    group_id?: string
  }
}

@Injectable({ providedIn: 'root' })
export class TapeArchiverService {
  private KEY = 'tape:backup-queue'
  private queue: TapeArchiveQueueItem[] = []

  constructor (
    private api: APIService,
    private sharedStorage: SharedStorageService,
    private translate: TranslateService,
    private identity: IdentityService,
    private tapeJobs: TapeJobsService,
    private notify: NotifyService,
  ) {
    if (!identity.loggedIn) {
      return
    }

    const queue = sharedStorage.get(this.KEY, [])
    for (let item of queue) {
      if (!item.path) {
        item = {
          path: item as string,
          include: 'all',
          options: {},
        }
      }
      this.queue.push(item as TapeArchiveQueueItem)
    }
  }

  clear (): void {
    this.queue = []
    this.saveQueue()
  }

  getQueue (): TapeArchiveQueueItem[] {
    return this.queue
  }

  enqueue (item: TapeArchiveQueueItem|string, notifyUser = false): void {
    if (item instanceof Object) {
      this.queue = [...this.queue, item]
    } else {
      this.queue = [...this.queue, {
        path: item,
        include: 'all',
        options: {},
      }]
    }
    this.saveQueue()

    if (notifyUser) {
      this.notify.info(
        this.translate.instant('Archive'),
        this.translate.instant('Queued for archiving'),
      )
    }
  }

  removeItem (item: TapeArchiveQueueItem): void {
    this.queue = this.queue.filter(x => x !== item)
    this.saveQueue()
  }

  async start (options: Omit<ArchiveEndpointRequest, 'source'>): Promise<void> {
    await this.api.tapeArchive.archiveToTape({
      archiveEndpointRequest: {
        ...options,
        source: this.getQueue(),
      },
    })
    this.tapeJobs.refresh()
  }

  private saveQueue () {
    this.sharedStorage.querySet(this.KEY, this.queue)
  }
}
